import { z } from 'zod'
import { ajouterJours, ajouterMois } from './calendrier'
import { formaterJour } from './dates'
import {
  DEVISE_PAR_DEFAUT,
  NATURES_SOIN,
  type NatureSoin,
  PAYEURS_SANTE,
  type PayeurSante,
  ROLES_PIECE_SOIN,
  type StatutPaiement,
  type StatutRemboursement,
} from './domaine'
import { TransitionInvalide } from './machine'
import { formaterMontant } from './montant'

// Santé du foyer : chaque soin d'un membre du foyer (consultation, semestre d'orthodontie,
// lunettes…) et ses remboursements, dans l'ordre : régime obligatoire, mutuelle du foyer, puis
// complément de l'autre parent (enfant de parents séparés : il le demande à sa mutuelle avec
// les pièces qu'on lui transmet, puis le reverse). Fonctions pures ; les services appliquent.
// Rien n'est demandé ni payé par l'appli : elle prépare le dossier (N1), un humain l'envoie.

const texte = z.string().trim().min(1)
const facultatif = <T extends z.ZodType>(schema: T) => schema.nullable().optional()
const id = z.uuid()
const jour = z.iso.date()
const centimes = z.number().int().nonnegative()
const heure = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Heure au format HH:MM')

// Saisie

export const beneficiaireCreation = z.object({
  entiteId: id,
  prenom: texte.max(80),
  nom: facultatif(texte.max(80)),
  dateNaissance: facultatif(jour),
  /** La personne de l'appli qu'il est (un parent) : ses rendez-vous d'agenda sont les siens. */
  personneId: facultatif(id),
  /** L'autre parent (tiers « particulier ») à qui transmettre les pièces du complément. */
  autreParentId: facultatif(id),
})
export type BeneficiaireCreation = z.infer<typeof beneficiaireCreation>

export const beneficiaireModification = beneficiaireCreation
  .omit({ entiteId: true })
  .partial()
  .extend({ actif: z.boolean().optional() })
export type BeneficiaireModification = z.infer<typeof beneficiaireModification>

export const couvertureCreation = z.object({
  beneficiaireId: id,
  payeur: z.enum(PAYEURS_SANTE),
  /** CPAM, mutuelle (tiers « organisme_sante ») ; pour l'autre parent, sa mutuelle si connue. */
  organismeId: facultatif(id),
  /** « Mutuelle de l'employeur », « carte Vitale de son père »… */
  libelle: facultatif(texte.max(120)),
  /** Ordre dans lequel elle intervient (1 : régime obligatoire). */
  rang: z.number().int().min(1).max(9),
})
export type CouvertureCreation = z.infer<typeof couvertureCreation>

export const couvertureModification = couvertureCreation
  .omit({ beneficiaireId: true })
  .partial()
  .extend({ actif: z.boolean().optional() })
export type CouvertureModification = z.infer<typeof couvertureModification>

const champsSoin = {
  /** Null : repéré sans savoir pour qui (agenda partagé), à préciser. */
  beneficiaireId: facultatif(id),
  date: jour,
  heure: facultatif(heure),
  nature: z.enum(NATURES_SOIN),
  /** « Semestre 3 », « verres progressifs ». */
  libelle: facultatif(texte.max(200)),
  praticien: facultatif(texte.max(200)),
  montantPayeCts: facultatif(centimes),
  devise: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .optional(),
  /** L'autre parent a avancé les frais : ce que la mutuelle du foyer rembourse lui revient. */
  avanceParAutreParent: z.boolean().optional(),
  /** Ni remboursement ni suite attendus (non remboursable, abandonné). */
  sansSuite: z.boolean().optional(),
  note: facultatif(texte.max(1000)),
}

export const soinCreation = z.object({ entiteId: id, ...champsSoin })
export type SoinCreation = z.infer<typeof soinCreation>

export const soinModification = z.object(champsSoin).partial()
export type SoinModification = z.infer<typeof soinModification>

export const lienPieceSoin = z.object({
  pieceId: id,
  role: z.enum(ROLES_PIECE_SOIN),
})
export type LienPieceSoin = z.infer<typeof lienPieceSoin>

export const remboursementModification = z
  .object({
    organismeId: facultatif(id),
    montantAttenduCts: facultatif(centimes),
  })
  .partial()
export type RemboursementModification = z.infer<typeof remboursementModification>

export const remboursementCreation = z.object({
  soinId: id,
  payeur: z.enum(PAYEURS_SANTE),
  organismeId: facultatif(id),
  montantAttenduCts: facultatif(centimes),
})
export type RemboursementCreation = z.infer<typeof remboursementCreation>

export const actionRemboursement = z.discriminatedUnion('action', [
  z.object({ action: z.literal('recevoir'), montantCts: centimes, date: jour.optional() }),
  z.object({ action: z.literal('refuser') }),
  z.object({ action: z.literal('sans_objet') }),
  z.object({ action: z.literal('rouvrir') }),
])
export type ActionRemboursement = z.infer<typeof actionRemboursement>

/** Dossier pour l'autre parent : un ou plusieurs soins de ses enfants, en une seule demande. */
export const dossierAutreParent = z.object({
  soinIds: z.array(id).min(1).max(20),
})
export type DossierAutreParent = z.infer<typeof dossierAutreParent>

// Lecture d'une pièce de santé (extraction par Claude) et d'un mail de rendez-vous (tri)

/** Ce qu'une pièce de santé dit des soins : une ligne par acte remboursé ou facturé. */
export const extractionSante = z.object({
  document: z.enum(['decompte', 'facture', 'ordonnance', 'devis', 'autre']),
  /** Qui a émis un décompte : régime obligatoire ou mutuelle ; null hors décompte. */
  payeur: z.enum(['regime_obligatoire', 'mutuelle']).nullable(),
  lignes: z
    .array(
      z.object({
        /** Prénom du patient tel qu'écrit sur la pièce. */
        beneficiaire: z.string().max(120).nullable(),
        dateSoins: jour.nullable(),
        nature: z.enum(NATURES_SOIN).nullable(),
        acte: z.string().max(200).nullable(),
        praticien: z.string().max(200).nullable(),
        payeCts: z.number().int().nullable(),
        baseCts: z.number().int().nullable(),
        rembourseCts: z.number().int().nullable(),
      }),
    )
    .max(60),
})
export type ExtractionSante = z.infer<typeof extractionSante>

/** Rendez-vous médical repéré dans un mail (confirmation, rappel). */
export const rendezVousSante = z.object({
  beneficiaire: z.string().max(120).nullable(),
  date: jour.nullable(),
  heure: heure.nullable(),
  praticien: z.string().max(200).nullable(),
  nature: z.enum(NATURES_SOIN).nullable(),
})
export type RendezVousSante = z.infer<typeof rendezVousSante>

// Transitions d'un remboursement (en données : un test par ligne)

export type EvenementRemboursement =
  /** Dossier préparé pour l'autre parent : la demande est partie (ou va partir). */
  | { type: 'demander' }
  | { type: 'recevoir' }
  | { type: 'refuser' }
  | { type: 'sans_objet' }
  | { type: 'rouvrir' }

/**
 * annuler_paiement : le paiement attendu qui l'accompagne (encaissement de l'autre parent)
 * est annulé, sauf s'il est déjà payé ou rapproché.
 */
export type EffetRemboursement = 'annuler_paiement'

type LigneRemboursement = {
  evenement: EvenementRemboursement['type']
  depuis: readonly StatutRemboursement[]
  vers: StatutRemboursement
  effets: readonly EffetRemboursement[]
}

export const TRANSITIONS_REMBOURSEMENT: readonly LigneRemboursement[] = [
  { evenement: 'demander', depuis: ['attendu'], vers: 'demande', effets: [] },
  { evenement: 'recevoir', depuis: ['attendu', 'demande', 'refuse'], vers: 'recu', effets: [] },
  {
    evenement: 'refuser',
    depuis: ['attendu', 'demande'],
    vers: 'refuse',
    effets: ['annuler_paiement'],
  },
  {
    evenement: 'sans_objet',
    depuis: ['attendu', 'demande', 'refuse'],
    vers: 'sans_objet',
    effets: ['annuler_paiement'],
  },
  {
    evenement: 'rouvrir',
    depuis: ['demande', 'recu', 'refuse', 'sans_objet'],
    vers: 'attendu',
    effets: ['annuler_paiement'],
  },
]

export function transitionRemboursement(
  etat: { statut: StatutRemboursement },
  evenement: EvenementRemboursement,
): { statut: StatutRemboursement; effets: EffetRemboursement[] } {
  const ligne = TRANSITIONS_REMBOURSEMENT.find(
    (l) => l.evenement === evenement.type && l.depuis.includes(etat.statut),
  )
  if (!ligne) throw new TransitionInvalide(etat.statut, evenement.type)
  return { statut: ligne.vers, effets: [...ligne.effets] }
}

// État d'un soin

export type RemboursementVu = {
  payeur: PayeurSante
  statut: StatutRemboursement
  montantAttenduCts: number | null
  montantRecuCts: number | null
  /** Paiement attendu lié (encaissement) : payé ou rapproché, il vaut réception. */
  paiementStatut: StatutPaiement | null
  paiementMontantCts: number | null
}

export type SoinVu = {
  date: string
  montantPayeCts: number | null
  avanceParAutreParent: boolean
  sansSuite: boolean
  /** Un paiement au profit de l'autre parent existe déjà (ce qui lui revient est reversé). */
  reverse?: boolean
}

export const ETATS_SOIN = [
  'a_venir',
  'a_completer',
  'attente_secu',
  'attente_mutuelle',
  'a_demander',
  'attente_autre_parent',
  'a_reverser',
  'solde',
  'sans_suite',
] as const
export type EtatSoin = (typeof ETATS_SOIN)[number]

/** Ce qui reste à faire ou à attendre : le tableau les met en avant. */
export const ETATS_SOIN_OUVERTS: readonly EtatSoin[] = [
  'a_completer',
  'attente_secu',
  'attente_mutuelle',
  'a_demander',
  'attente_autre_parent',
  'a_reverser',
]

/** Reçu : marqué reçu, ou son encaissement payé ou rapproché. Le montant reçu, sinon. */
export function montantRecu(r: RemboursementVu): number | null {
  if (r.statut === 'recu') return r.montantRecuCts ?? r.paiementMontantCts ?? 0
  if (r.paiementStatut === 'paye' || r.paiementStatut === 'rapproche')
    return r.montantRecuCts ?? r.paiementMontantCts ?? 0
  return null
}

const ouvert = (r: RemboursementVu) =>
  montantRecu(r) === null && (r.statut === 'attendu' || r.statut === 'demande')

export type BilanSoin = {
  etat: EtatSoin
  /** Remboursé par le régime obligatoire et les mutuelles du foyer. */
  rembourseCts: number
  /** Complément reçu de l'autre parent. */
  complementCts: number
  /** Reste à charge effectif après Sécu et mutuelle : ce qu'on demande à l'autre parent. */
  resteApresMutuelleCts: number | null
  /** Reste à charge final, tout ce qui est reçu déduit. */
  resteACts: number | null
}

/**
 * État d'un soin, du plus urgent au plus avancé. Avancé par l'autre parent, rien ne sort de
 * notre poche : ce que la mutuelle du foyer rembourse lui revient (« à reverser »).
 */
export function bilanSoin(
  soin: SoinVu,
  remboursements: readonly RemboursementVu[],
  aujourdhui: string,
): BilanSoin {
  const foyer = remboursements.filter((r) => r.payeur !== 'autre_parent')
  const parent = remboursements.filter((r) => r.payeur === 'autre_parent')
  const somme = (liste: readonly RemboursementVu[]) =>
    liste.reduce((n, r) => n + (montantRecu(r) ?? 0), 0)
  const rembourseCts = somme(foyer)
  const complementCts = somme(parent)
  const paye = soin.montantPayeCts
  const resteApresMutuelleCts =
    paye === null || soin.avanceParAutreParent ? null : Math.max(0, paye - rembourseCts)
  const resteACts =
    paye === null || soin.avanceParAutreParent
      ? null
      : Math.max(0, paye - rembourseCts - complementCts)
  const bilan = (etat: EtatSoin): BilanSoin => ({
    etat,
    rembourseCts,
    complementCts,
    resteApresMutuelleCts,
    resteACts,
  })

  if (soin.sansSuite) return bilan('sans_suite')
  if (soin.date > aujourdhui && paye === null) return bilan('a_venir')
  if (paye === null && !soin.avanceParAutreParent) return bilan('a_completer')
  if (foyer.some((r) => r.payeur === 'regime_obligatoire' && ouvert(r)))
    return bilan('attente_secu')
  if (foyer.some((r) => r.payeur === 'mutuelle' && ouvert(r))) return bilan('attente_mutuelle')
  if (soin.avanceParAutreParent)
    return bilan(rembourseCts > 0 && !soin.reverse ? 'a_reverser' : 'solde')
  const restant = resteApresMutuelleCts ?? 0
  if (restant > 0 && parent.some((r) => r.statut === 'attendu' && montantRecu(r) === null))
    return bilan('a_demander')
  if (parent.some((r) => r.statut === 'demande' && montantRecu(r) === null))
    return bilan('attente_autre_parent')
  return bilan('solde')
}

// Remboursements d'un nouveau soin, d'après les couvertures du membre du foyer

export type CouvertureVue = {
  payeur: PayeurSante
  organismeId: string | null
  rang: number
  actif: boolean
}

/**
 * Une ligne par couverture active, dans l'ordre. Avancé par l'autre parent, sa propre
 * couverture ne joue pas de notre côté (c'est lui qui la demande).
 */
export function remboursementsParDefaut(
  couvertures: readonly CouvertureVue[],
  soin: { avanceParAutreParent: boolean },
): { payeur: PayeurSante; organismeId: string | null; rang: number }[] {
  return [...couvertures]
    .filter((c) => c.actif)
    .filter((c) => !(soin.avanceParAutreParent && c.payeur === 'autre_parent'))
    .sort((a, b) => a.rang - b.rang)
    .map(({ payeur, organismeId, rang }) => ({ payeur, organismeId, rang }))
}

// Rendez-vous médicaux repérés dans un agenda, sans modèle

const sansAccents = (t: string) =>
  t
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[’'`]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

/** Mots qui désignent un soin, et la nature qu'ils donnent (le premier trouvé l'emporte). */
const MOTS_SOIN: readonly [RegExp, NatureSoin][] = [
  [/\borthodont\w*|\bortho\b|\bbagues?\b|\bappareil dentaire\b/, 'orthodontie'],
  [/\bdentiste\b|\bdentaire\b|\bdetartrage\b|\bstomato\w*|\bparodont\w*/, 'dentaire'],
  [/\bophtalmo\w*|\borthoptist\w*|\bopticien\w*|\blunettes\b|\blentilles\b/, 'optique'],
  [/\bkine\w*|\bosteo\w*|\borthophon\w*|\bpodolog\w*|\bpsychomot\w*/, 'kinesitherapie'],
  [/\bradio\w*|\bechograph\w*|\birm\b|\bscanner\b|\bmammograph\w*/, 'imagerie'],
  [/\blabo\w*|\bprise de sang\b|\banalyses?\b|\bbilan sanguin\b/, 'analyses'],
  [/\bhopital\b|\bclinique\b|\bchirurg\w*|\bhospitalis\w*/, 'hospitalisation'],
  [
    /\bdocteur\b|\bdr\b|\bmedecin\b|\bpediatre\b|\bgeneraliste\b|\bdermato\w*|\bgyneco\w*|\bcardio\w*|\borl\b|\ballergolog\w*|\bpsychiatre\b|\bsage[- ]femme\b|\bdoctolib\b|\bvaccin\w*|\bconsultation\b|\bteleconsultation\b|\bcentre de sante\b/,
    'consultation',
  ],
]

/** Les intitulés d'agenda qui ressemblent à un soin sans en être un. */
const FAUX_AMIS = /\banniversaire\b|\breunion\b|\bcall\b|\bvisio\b(?!.*\bmedecin\b)/

/** Ce qu'un événement d'agenda dit d'un soin (titre et lieu seulement), ou null. */
export function rendezVousMedical(e: {
  titre: string | null
  lieu: string | null
}): { nature: NatureSoin; praticien: string | null } | null {
  const titre = sansAccents(e.titre ?? '')
  const lieu = sansAccents(e.lieu ?? '')
  if (!titre) return null
  if (FAUX_AMIS.test(titre) && !/\bdr\b|\bdocteur\b|\bmedecin\b/.test(titre)) return null
  for (const [motif, nature] of MOTS_SOIN) {
    if (motif.test(titre) || motif.test(lieu)) {
      const praticien = /\b(?:dr|docteur)\.?\s+([\p{L}-]+(?:\s+[\p{L}-]+)?)/iu.exec(e.titre ?? '')
      return { nature, praticien: praticien ? `Dr ${praticien[1]}` : null }
    }
  }
  return null
}

// Lignes de remboursement lues sur l'espace adhérent d'une mutuelle (collecteur, sans modèle)

/**
 * Une ligne du tableau des paiements d'une mutuelle, lue par l'adaptateur du portail : données,
 * jamais consignes. `ref` : clé de la ligne chez le fournisseur (dates et montants), sans chemin
 * ni adresse ; `statut` : texte du portail, interprété ici seulement (`partsRegleesDeLaLigne`).
 */
export const ligneSantePortail = z
  .object({
    ref: z
      .string()
      .regex(/^[\p{L}\p{N}][\p{L}\p{N}.-]{0,119}$/u, 'Référence de ligne illisible.')
      .refine((r) => !r.includes('..'), 'Référence de ligne illisible.'),
    beneficiaire: z.string().max(120).nullable(),
    dateSoins: jour,
    dateReglement: jour.nullable(),
    acte: z.string().max(200).nullable(),
    fraisCts: centimes.nullable(),
    rembourseRoCts: centimes.nullable(),
    rembourseMutuelleCts: centimes.nullable(),
    statut: z.string().max(60).nullable(),
  })
  .strict()
export type LigneSantePortail = z.infer<typeof ligneSantePortail>

/** Un lot de lignes, comme l'ordinateur du titulaire l'envoie. */
export const lignesSantePortail = z.object({ lignes: z.array(ligneSantePortail).max(500) }).strict()

/** Les lignes lues, revalidées : une fois chacune (par référence), 500 au plus ; le reste oublié. */
export function normaliserLignesSante(lues: readonly unknown[]): LigneSantePortail[] {
  const vues = new Set<string>()
  const sortie: LigneSantePortail[] = []
  for (const l of lues) {
    const v = ligneSantePortail.safeParse(l)
    if (!v.success || vues.has(v.data.ref)) continue
    vues.add(v.data.ref)
    sortie.push(v.data)
    if (sortie.length === 500) break
  }
  return sortie
}

/** La nature d'un soin d'après l'intitulé de l'acte (« kinesitherapie »), ou null. */
export function natureDeLActe(acte: string | null): NatureSoin | null {
  const t = sansAccents(acte ?? '')
  if (!t) return null
  if (/\bpharma\w*|\bmedicament\w*/.test(t)) return 'pharmacie'
  return MOTS_SOIN.find(([motif]) => motif.test(t))?.[1] ?? null
}

/**
 * Ce qu'une ligne dit avoir été remboursé : rien tant que le statut ne dit pas « réglé » ; la
 * part du régime obligatoire si elle n'est pas nulle ; celle de la mutuelle aussi, sauf quand le
 * statut ne cite que la Sécurité sociale (« réglé SS » : la mutuelle n'a pas encore payé).
 */
export function partsRegleesDeLaLigne(
  l: LigneSantePortail,
): { payeur: 'regime_obligatoire' | 'mutuelle'; montantCts: number; date: string }[] {
  const statut = ` ${sansAccents(l.statut ?? '')} `
  if (!l.dateReglement || !/\bregle/.test(statut)) return []
  const ssSeule =
    /\bss\b|\bsecu\w*|securite sociale/.test(statut) && !/aon|mutuelle|complementaire/.test(statut)
  const parts: ReturnType<typeof partsRegleesDeLaLigne> = []
  if (l.rembourseRoCts)
    parts.push({
      payeur: 'regime_obligatoire',
      montantCts: l.rembourseRoCts,
      date: l.dateReglement,
    })
  if (l.rembourseMutuelleCts && !ssSeule)
    parts.push({ payeur: 'mutuelle', montantCts: l.rembourseMutuelleCts, date: l.dateReglement })
  return parts
}

export type BeneficiaireRef = { id: string; prenom: string; personneId: string | null }

/**
 * Le membre du foyer que désigne un texte : son prénom, en mot entier (« RDV ortho Paul »). Deux
 * prénoms cités, ou aucun : la personne de l'appli à qui est l'agenda si elle est membre, sinon
 * personne (le soin reste à préciser).
 */
export function beneficiaireDesigne(
  textes: readonly (string | null | undefined)[],
  beneficiaires: readonly BeneficiaireRef[],
  proprietaireId: string | null = null,
): string | null {
  const t = ` ${sansAccents(textes.filter(Boolean).join(' '))} `.replace(/[^a-z0-9]+/g, ' ')
  const cites = beneficiaires.filter((b) => {
    const prenom = sansAccents(b.prenom).replace(/[^a-z0-9]+/g, ' ')
    return prenom.length > 1 && t.includes(` ${prenom} `)
  })
  if (cites.length === 1) return cites[0]!.id
  if (cites.length > 1) return null
  return beneficiaires.find((b) => proprietaireId && b.personneId === proprietaireId)?.id ?? null
}

// Pièces de santé : une ligne lue retrouve son soin

export type SoinCandidat = {
  id: string
  beneficiaireId: string | null
  date: string
  nature: NatureSoin
  montantPayeCts: number | null
  /** Jours de ses factures. */
  joursFactures?: readonly string[]
}

/**
 * Le soin d'une ligne de pièce : même membre du foyer, même jour (à trois jours près pour un
 * soin repéré d'avance sans montant, le décompte datant parfois l'acte autrement) ; à défaut
 * d'un seul, même nature ou même montant payé. Un soin facturé pour une période (semestre
 * d'orthodontie) : la facture le date du début de la période, la mutuelle et le décompte du jour
 * de la facture ; même membre, même nature et même montant payé, le jour de l'un étant celui de
 * la facture de l'autre. Null : nouveau soin.
 */
export function soinDeLaLigne(
  ligne: {
    beneficiaireId: string | null
    date: string
    nature: NatureSoin | null
    payeCts: number | null
    /** Jour de la facture d'où vient la ligne. */
    jourFacture?: string | null
  },
  candidats: readonly SoinCandidat[],
): string | null {
  const memes = candidats.filter(
    (s) => s.beneficiaireId === ligne.beneficiaireId && s.date === ligne.date,
  )
  const choisir = (liste: readonly SoinCandidat[]) => {
    if (liste.length === 1) return liste[0]!.id
    if (liste.length === 0) return null
    const parMontant = liste.filter(
      (s) => ligne.payeCts !== null && s.montantPayeCts === ligne.payeCts,
    )
    if (parMontant.length === 1) return parMontant[0]!.id
    const parNature = liste.filter((s) => ligne.nature !== null && s.nature === ligne.nature)
    if (parNature.length === 1) return parNature[0]!.id
    return null
  }
  const exact = choisir(memes)
  if (exact || memes.length > 1) return exact
  const proches = candidats.filter(
    (s) =>
      s.beneficiaireId === ligne.beneficiaireId &&
      s.montantPayeCts === null &&
      Math.abs(Date.parse(s.date) - Date.parse(ligne.date)) <= 3 * 86_400_000 &&
      (ligne.nature === null || s.nature === ligne.nature),
  )
  if (proches.length === 1) return proches[0]!.id
  if (ligne.nature === null || ligne.payeCts === null) return null
  const parFacture = candidats.filter(
    (s) =>
      s.beneficiaireId === ligne.beneficiaireId &&
      s.nature === ligne.nature &&
      s.montantPayeCts === ligne.payeCts &&
      (s.date === ligne.jourFacture || (s.joursFactures ?? []).includes(ligne.date)),
  )
  return parFacture.length === 1 ? parFacture[0]!.id : null
}

/** Le rôle d'une pièce de santé pour ses soins. */
export function rolePieceSante(document: ExtractionSante['document']) {
  return document === 'facture'
    ? ('facture' as const)
    : document === 'ordonnance'
      ? ('ordonnance' as const)
      : document === 'decompte'
        ? ('decompte' as const)
        : document === 'devis'
          ? ('devis' as const)
          : ('autre' as const)
}

// Dossier pour l'autre parent (N1 : préparé ici, envoyé par un humain)

export const LIBELLES_NATURE_SOIN: Record<NatureSoin, string> = {
  consultation: 'Consultation',
  dentaire: 'Soins dentaires',
  orthodontie: 'Orthodontie',
  optique: 'Optique',
  pharmacie: 'Pharmacie',
  analyses: 'Analyses',
  imagerie: 'Imagerie',
  kinesitherapie: 'Kinésithérapie',
  hospitalisation: 'Hospitalisation',
  autre: 'Soin',
}

export type SoinDuDossier = {
  beneficiaire: string
  date: string
  nature: NatureSoin
  libelle: string | null
  praticien: string | null
  payeCts: number
  rembourseCts: number
  resteCts: number
  devise?: string
}

export type PreuveManquante = {
  cle: 'facture' | 'regime_obligatoire' | 'mutuelle'
  libelle: string
}

/**
 * Ce qui manque au dossier d'un soin pour l'autre parent ; sans ces pièces, il ne part pas
 * (décision de Pierre-Marie du 26/10/2026) : la facture, et la preuve de chaque remboursement
 * reçu de la Sécurité sociale et de la mutuelle (le décompte noté sur le remboursement, ou un
 * décompte de ce payeur lié au soin). Un payeur qui n'a rien versé n'a rien à prouver.
 */
export function preuvesManquantes(s: {
  pieces: readonly { role: string; payeur: string | null }[]
  remboursements: readonly {
    payeur: string
    statut: string
    montantRecuCts: number | null
    pieceId: string | null
    organisme: string | null
  }[]
}): PreuveManquante[] {
  const manque: PreuveManquante[] = []
  if (!s.pieces.some((p) => p.role === 'facture'))
    manque.push({ cle: 'facture', libelle: 'facture ou note d’honoraires' })
  for (const payeur of ['regime_obligatoire', 'mutuelle'] as const) {
    const recus = s.remboursements.filter(
      (r) => r.payeur === payeur && r.statut === 'recu' && (r.montantRecuCts ?? 0) > 0,
    )
    if (recus.length === 0) continue
    const prouve =
      recus.every((r) => r.pieceId !== null) ||
      s.pieces.some((p) => p.role === 'decompte' && p.payeur === payeur)
    if (prouve) continue
    const organisme = recus.find((r) => r.organisme)?.organisme
    manque.push({
      cle: payeur,
      libelle:
        payeur === 'regime_obligatoire'
          ? 'décompte de la Sécurité sociale'
          : `décompte de la mutuelle${organisme ? ` (${organisme})` : ''}`,
    })
  }
  return manque
}

/** Les portails dont le collecteur rapporte la preuve d'un payeur (décompte, relevé mensuel). */
export const PORTAILS_PREUVE_SANTE: Readonly<
  Record<'regime_obligatoire' | 'mutuelle', readonly string[]>
> = {
  regime_obligatoire: ['ameli'],
  mutuelle: ['aon'],
}

/** Objet et corps du courriel à l'autre parent : sobre, sans engagement, montants exacts. */
export function courrielDossier(e: {
  destinataire: string
  soins: readonly SoinDuDossier[]
  nomPiece: string
  manquantes: readonly string[]
}): { objet: string; corps: string; totalCts: number } {
  const devise = e.soins[0]?.devise ?? DEVISE_PAR_DEFAUT
  const totalCts = e.soins.reduce((n, s) => n + s.resteCts, 0)
  const prenoms = [...new Set(e.soins.map((s) => s.beneficiaire))]
  const lignes = e.soins.map((s) =>
    [
      `- ${s.beneficiaire}, ${formaterJour(s.date)} : ${s.libelle ?? LIBELLES_NATURE_SOIN[s.nature]}`,
      s.praticien ? ` (${s.praticien})` : '',
      ` ; payé ${formaterMontant(s.payeCts, devise)}, remboursé ${formaterMontant(s.rembourseCts, devise)} par la Sécurité sociale et la mutuelle, reste à charge ${formaterMontant(s.resteCts, devise)}.`,
    ].join(''),
  )
  const corps = [
    'Bonjour,',
    '',
    `Voici les pièces pour demander à ta mutuelle le remboursement du reste à charge des soins de ${prenoms.join(' et ')} :`,
    ...lignes,
    '',
    `Reste à charge total : ${formaterMontant(totalCts, devise)}.`,
    '',
    `Le dossier joint (${e.nomPiece}) réunit les factures, ordonnances et décomptes de la Sécurité sociale et de notre mutuelle qui montrent ce reste à charge.`,
    e.manquantes.length > 0
      ? `[à compléter : pièces encore absentes du dossier : ${e.manquantes.join(', ')}]`
      : null,
    'Peux-tu me reverser le remboursement de ta mutuelle quand tu l’auras reçu ?',
    '',
    'Merci,',
  ]
    .filter((l) => l !== null)
    .join('\n')
  return {
    objet: `Remboursement complémentaire des soins de ${prenoms.join(' et ')}`,
    corps,
    totalCts,
  }
}

/** Délai laissé à l'autre parent pour reverser le complément (date prévue de l'encaissement). */
export const DELAI_COMPLEMENT_JOURS = 45

export function datePrevueComplement(aujourdhui: string): string {
  return ajouterJours(aujourdhui, DELAI_COMPLEMENT_JOURS)
}

// Rappel mensuel : relever les décomptes (aucun identifiant confié, l'humain lance l'agent)

/** Le mois dont on relève les décomptes : le précédent, au format canonique « 2026-09 ». */
export function moisDesDecomptes(aujourdhui: string): string {
  return ajouterMois(`${aujourdhui.slice(0, 7)}-01`, -1).slice(0, 7)
}

export function objectifDecomptes(e: { mois: string; organismes: readonly string[] }): string {
  const [annee, mois] = e.mois.split('-')
  const sites =
    e.organismes.length > 0
      ? e.organismes.join(', ')
      : 'l’Assurance maladie (ameli.fr) et la mutuelle'
  return `Télécharger les décomptes de remboursement de santé de ${mois}/${annee} (relevé mensuel et détail par soin) sur ${sites}, puis les déposer dans l’appli admin PMVB (page Photo ou Pièces, indication « santé »).`
}
